import { useEffect, useState } from "react";

/**
 * useOrgProfile — `window.__orgProfile`, but reactive.
 *
 * Layout writes the org profile onto `window` inside an async fetch and
 * dispatches nothing. Anything that reads it synchronously during render is
 * therefore racing that fetch: on a cold load the read returns undefined, the
 * feature gate says "no", and because a plain global is not reactive React
 * never re-renders to find out otherwise. The control stays hidden for the rest
 * of the session. On a warm navigation the profile is already there and the same
 * control appears — which is exactly the "sometimes it renders" behaviour.
 *
 * This polls until the profile lands and then stops. Polling rather than an
 * event because Layout is shared by every page in the app; adding a dispatch
 * there to fix two buttons on one screen is the more invasive change, and this
 * hook is correct no matter which of the three Layouts did the writing.
 *
 * `__orgProfileFetchedAt` is watched too, so a profile refresh (plan change,
 * payment success) re-flows through the gates instead of going stale.
 */
const POLL_MS = 150;
const GIVE_UP_MS = 15000;

export function useOrgProfile() {
  const [profile, setProfile] = useState(
    typeof window !== "undefined" ? window.__orgProfile || null : null
  );
  const [stamp, setStamp] = useState(
    typeof window !== "undefined" ? window.__orgProfileFetchedAt || 0 : 0
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let alive = true;
    const started = Date.now();

    const check = () => {
      if (!alive) return;
      const next = window.__orgProfile || null;
      const at = window.__orgProfileFetchedAt || 0;
      if (at !== stamp || next !== profile) {
        setProfile(next);
        setStamp(at);
      }
      // Stop once we have it. Keep going only while it is still missing, and
      // never past the give-up window — a logged-out or errored fetch must not
      // leave a timer running for the life of the tab.
      if (!next && Date.now() - started < GIVE_UP_MS) {
        timer = setTimeout(check, POLL_MS);
      }
    };

    let timer = setTimeout(check, POLL_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [profile, stamp]);

  return profile;
}

export default useOrgProfile;
